import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import EmailOtpVerify from "../components/EmailOtpVerify";
import GoogleButton from "../components/GoogleButton";

const inputCls = "w-full border border-zinc-300 px-3 py-2.5 text-sm focus:border-zinc-900 focus:outline-none";

function TextField({ label, testId, ...props }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 block mb-1.5">{label}</label>
      <input required className={inputCls} data-testid={testId} {...props} />
    </div>
  );
}

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [pendingEmail, setPendingEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) { toast.error("Please accept the Terms and Privacy Policy to continue"); return; }
    setBusy(true);
    try {
      const result = await register({ ...form, role: "student" });
      if (result?.need_verify) {
        setPendingEmail(result.email);
        toast.success("Check your email for a 6-digit code");
      } else {
        navigate("/complete-profile");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white" data-testid="register-page">
      <div className="hidden lg:flex flex-col justify-between bg-zinc-900 text-white p-12 grid-bg">
        <Link to="/" className="font-display text-2xl font-bold">EXAM<span className="text-blue-400">/</span>OS</Link>
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-400 mb-4 font-semibold">Get started</div>
          <div className="font-display text-4xl font-bold tracking-tight leading-tight">
            {pendingEmail ? "One quick check — verify your email." : "Set up your account in under a minute."}
          </div>
          <div className="mt-6 text-sm text-zinc-400 max-w-md">
            Sign up with Gmail or any email address. You&rsquo;ll pick your role and institution in the next step.
          </div>
        </div>
        <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500">Free during preview · 500 bonus credits</div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        {pendingEmail ? (
          <EmailOtpVerify
            email={pendingEmail}
            onVerified={() => navigate("/complete-profile", { replace: true })}
            onChangeEmail={() => setPendingEmail("")}
          />
        ) : (
          <div className="w-full max-w-sm">
            <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3 font-semibold">Create account</div>
            <h1 className="font-display text-3xl font-bold tracking-tight mb-2">Register</h1>
            <p className="text-sm text-zinc-500 mb-8">Use Google or your email address.</p>

            <GoogleButton label="Sign up with Google" testId="register-google-btn" />

            <div className="my-6 flex items-center gap-4 text-[10px] uppercase tracking-[0.2em] text-zinc-400">
              <div className="flex-1 h-px bg-zinc-200" />
              or with email
              <div className="flex-1 h-px bg-zinc-200" />
            </div>

            <form onSubmit={submit} className="space-y-4" data-testid="register-form">
              <TextField label="Full name" value={form.name} onChange={set("name")} testId="register-name-input" />
              <TextField label="Email" type="email" value={form.email} onChange={set("email")} testId="register-email-input" />
              <TextField label="Password" type="password" minLength={6} value={form.password} onChange={set("password")} testId="register-password-input" />
              <label className="flex items-start gap-2 text-xs text-zinc-600 leading-relaxed cursor-pointer">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-zinc-900 cursor-pointer flex-shrink-0"
                  data-testid="register-consent-checkbox"
                  required
                />
                <span>
                  I agree to the{" "}
                  <Link to="/terms" target="_blank" className="link-underline font-medium text-zinc-900" data-testid="register-terms-link">Terms of Service</Link>
                  {" "}and{" "}
                  <Link to="/privacy" target="_blank" className="link-underline font-medium text-zinc-900" data-testid="register-privacy-link">Privacy Policy</Link>.
                </span>
              </label>
              <button
                type="submit"
                disabled={busy || !consent}
                className="w-full bg-zinc-900 text-white py-3 text-sm font-medium hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                data-testid="register-submit-btn"
              >
                {busy ? "Creating…" : "Create account"}
              </button>
            </form>

            <div className="mt-8 text-xs text-zinc-500">
              Already have an account?{" "}
              <Link to="/login" className="link-underline font-medium text-zinc-900" data-testid="register-go-login">Sign in</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
